// ============================================================
// TMR Portal Admin — CSV que o Excel em português abre com dois cliques
// ============================================================
import type { ExportColumn, ExportValue } from './types';

/**
 * Separator. `;` and not `,` because Excel in a pt-BR locale reads `,` as the decimal separator and puts
 * the whole row in the first column — the file is technically correct and unusable, which is the worst
 * combination. `;` is what every other Brazilian system emits, and what Excel expects here.
 */
const SEPARATOR = ';';

/** CRLF: the line ending the CSV RFC names, and the one Excel is happiest with. */
const NEWLINE = '\r\n';

/**
 * UTF-8 byte order mark. Without it Excel reads the file as the system code page and every accent turns
 * to mojibake — "São Paulo" becomes "SÃ£o Paulo" on the first row anybody looks at.
 *
 * Written as an escape and exported, never typed literally: it is invisible in a diff and in an editor,
 * and a test that retyped it would be asserting against a character nobody can see.
 */
export const UTF8_BOM = '\uFEFF';

/** How a value reads in a cell before quoting. Dates go out ISO, which sorts and re-imports. */
function toText(value: ExportValue): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

/**
 * Quotes a field when it could otherwise break the row.
 *
 * A leading `=`, `+`, `-` or `@` is prefixed with a single quote: spreadsheets treat those as the start of
 * a formula, so an innocent value can execute when the file is opened. Nothing in this portal should be
 * able to write a formula into somebody's Excel by being called `=cmd`.
 */
function escape(text: string): string {
  const guarded = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /["\r\n;]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/**
 * Serializes rows into CSV — hand-rolled, because a correct CSV is thirty lines and a dependency is
 * forever.
 *
 * @param rows The rows to write, already filtered and ordered as the screen had them.
 * @param columns Header and reader for each column.
 */
export function toCsv<T>(rows: readonly T[], columns: readonly ExportColumn<T>[]): string {
  const header = columns.map((column) => escape(column.header)).join(SEPARATOR);
  const body = rows.map(
    (row) => columns.map((column) => escape(toText(column.value(row)))).join(SEPARATOR),
  );

  return UTF8_BOM + [header, ...body].join(NEWLINE) + NEWLINE;
}
